import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FormStatus,
  SimpleEvent,
  ValidationContext,
  ValidationType,
} from '~/components/hoc/withValidation';
import pushToGtmHelper from '~/helpers/pushToGtm.helper';
import useTranslate from '~/hooks/useTranslate';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';
import SpinnerCircularSmall from '../Spinner/SpinnerCircularSmall';
import InputTooltip from './InputTooltip';
import {
  InputWrapper,
  LoadingSpinnerWrapper,
  RightText,
  InputSupportContainer,
  Input,
  LeftText,
} from './InputField.styled';
import { Body, Label, Stack } from '@qred/components-library';

interface InputFieldProps extends React.ComponentPropsWithoutRef<'input'> {
  label?: string | React.ReactNode;
  labelColor?: string;
  validationType?: ValidationType;
  maxLength?: number;
  characterCounter?: boolean;
  disabled?: boolean;
  rightText?: string | JSX.Element;
  leftText?: string;
  dataCy?: string;
  isLoading?: boolean;
  showValidationInfoWhenUntouched?: boolean;
  name: string;
  explanation?: string | React.ReactNode;
  thin?: boolean;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (props, ref) => {
    const {
      name,
      label,
      labelColor,
      placeholder,
      defaultValue,
      value,
      type,
      validationType,
      maxLength,
      characterCounter,
      disabled = false,
      onChange,
      onBlur,
      rightText = '',
      isLoading = false,
      showValidationInfoWhenUntouched,
      explanation,
      leftText = '',
      ...rest
    } = props;

    const [touched, setTouched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [validationMessageType, setValidationMessageType] = useState<
      'error' | 'info' | null
    >(null);
    const validationMessageRef = useRef<HTMLSpanElement>(null);
    const inputSupportContainerRef = useRef<HTMLDivElement>(null);
    const rightContainerRef = useRef<HTMLDivElement>(null);
    const leftContainerRef = useRef<HTMLDivElement>(null);
    const {
      formStatus,
      onChangeHOC,
      removePropertyFromValidationErrors,
      validationErrors,
    } = useContext(ValidationContext);
    const translate = useTranslate();

    const id = `${name}-input-id`;

    useEffect(() => {
      const fakeEvent: SimpleEvent = {
        target: {
          name,
          value: value || defaultValue,
          dataset: { validationType },
        },
      };

      onChangeHOC(fakeEvent);

      // This clean up is needed to remove the property from errors if input field is no longer shown
      return () => {
        removePropertyFromValidationErrors(name);
      };
    }, [value, defaultValue]);

    useEffect(() => {
      const stringValue = value?.toString();
      const wasAutoFilled = !touched && Boolean(stringValue) && !isFocused;

      const shouldShowValidationMessage =
        (showValidationInfoWhenUntouched ||
          touched ||
          wasAutoFilled ||
          formStatus === FormStatus.SUBMITTED) &&
        validationType &&
        validationErrors[name];

      if (shouldShowValidationMessage) {
        setValidationMessageType(
          showValidationInfoWhenUntouched &&
            !touched &&
            formStatus === FormStatus.IDLE
            ? 'info'
            : 'error'
        );
      } else {
        setValidationMessageType(null);
      }
    }, [
      value,
      touched,
      isFocused,
      formStatus,
      validationErrors,
      showValidationInfoWhenUntouched,
    ]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setTouched(true);
      onBlur && onBlur(e);
      if (validationMessageType) {
        const gtmValue = value
          ? 'q_user_a_input_field_format_error'
          : 'q_user_a_input_field_empty_error';
        pushToGtmHelper(gtmValue);
      }
    };

    const rightContainerWidth = rightContainerRef?.current?.clientWidth;
    const leftContainerWidth = leftContainerRef?.current?.clientWidth;

    return (
      <Stack spacing="xs" w="100%">
        {explanation ? (
          <InputTooltip label={label} tooltipCopy={explanation} />
        ) : label && typeof label === 'string' ? (
          <Label size="md">{label}</Label>
        ) : (
          label
        )}
        <Stack spacing="sm">
          <InputWrapper>
            <Input
              {...rest}
              data-cy={props.dataCy}
              type={type || 'text'}
              id={id}
              ref={ref}
              data-validation-type={validationType}
              name={name}
              placeholder={placeholder}
              defaultValue={defaultValue}
              value={value}
              onChange={onChange}
              onBlur={handleBlur}
              onFocus={() => setIsFocused(true)}
              maxLength={maxLength}
              disabled={disabled}
              rightText={!!rightText}
              leftText={!!leftText}
              /* Getting round the problem of input elements not re-rendered when default value is async by force-updating  */
              /* https://github.com/facebook/react/issues/4101 */
              key={`input-key:${defaultValue}`}
              className={
                validationMessageType
                  ? `has-${validationMessageType}`
                  : props.className ?? ''
              }
              isLoading={isLoading}
              rightContainerWidth={rightContainerWidth}
              leftContainerWidth={leftContainerWidth}
            />
            {isLoading && (
              <LoadingSpinnerWrapper ref={rightContainerRef}>
                <SpinnerCircularSmall />
              </LoadingSpinnerWrapper>
            )}
            {rightText && (
              <RightText ref={rightContainerRef}>{rightText}</RightText>
            )}
            {leftText && (
              <LeftText ref={leftContainerRef} value={value}>
                {leftText}
              </LeftText>
            )}
          </InputWrapper>
          {(validationMessageType ||
            (maxLength && value && characterCounter)) && (
            <InputSupportContainer ref={inputSupportContainerRef}>
              {validationMessageType && (
                <ValidationErrorMessage
                  ref={validationMessageRef}
                  type={validationMessageType}
                >
                  {translate(
                    `${
                      validationMessageType === 'error'
                        ? 'ValidationErrors'
                        : 'ValidationInfo'
                    }.${validationType?.split('_')[0]}`
                  )}
                </ValidationErrorMessage>
              )}
              {maxLength && value && characterCounter && (
                <Body size="sm" ml={'auto'}>{`${
                  value?.toString().length
                }/${maxLength}`}</Body>
              )}
            </InputSupportContainer>
          )}
        </Stack>
      </Stack>
    );
  }
);

export default InputField;
